// Generic picker dialog reused for "Add child resource" (filtered to valid
// children of the source node) and "Move to..." (filtered to valid new
// parents for an existing node).
//
// Pass an `options` array of { id, label, sub?, disabled?, tier? } and an
// onPick(option) callback.

import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";

export default function PickerDialog({
  open,
  onClose,
  title,
  description,
  options,
  emptyMessage = "No valid options.",
  onPick,
  testid = "picker-dialog",
}) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sub || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid={testid}
        className="max-w-[520px]"
        style={{
          background: "var(--color-background-primary)",
          border: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        <DialogHeader>
          <DialogTitle
            style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription
              style={{ fontSize: 13, color: "var(--color-text-secondary)" }}
            >
              {description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div
          className="flex items-center gap-2 mt-2"
          style={{
            background: "var(--color-background-secondary)",
            border: "1px solid var(--color-border-primary)",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          <Search size={13} color="var(--color-text-tertiary)" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            autoFocus
            data-testid={`${testid}-search`}
            style={{
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--color-text-primary)",
              fontSize: 13,
              width: "100%",
            }}
          />
        </div>

        <div
          style={{
            maxHeight: 360,
            overflowY: "auto",
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {filtered.length === 0 && (
            <p
              style={{
                fontSize: 12,
                color: "var(--color-text-tertiary)",
                padding: 10,
              }}
            >
              {emptyMessage}
            </p>
          )}
          {filtered.map((opt) => (
            <button
              key={opt.id}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (opt.disabled) return;
                onPick?.(opt);
              }}
              data-testid={`${testid}-option-${opt.id}`}
              className="motion-micro"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                width: "100%",
                background: opt.disabled ? "transparent" : "var(--color-background-secondary)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: 8,
                padding: "10px 12px",
                cursor: opt.disabled ? "not-allowed" : "pointer",
                textAlign: "left",
                opacity: opt.disabled ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!opt.disabled) e.currentTarget.style.borderColor = "var(--color-accent)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--color-border-primary)";
              }}
            >
              <span
                style={{ fontSize: 13.5, fontWeight: 500, color: "var(--color-text-primary)" }}
              >
                {opt.label}
              </span>
              {opt.sub && (
                <span
                  style={{ fontSize: 11.5, color: "var(--color-text-tertiary)" }}
                >
                  {opt.sub}
                </span>
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
